import type { ButtonHTMLAttributes, ReactNode } from "react";
import { haptic } from "@/lib/telegram";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
}

const SIZE: Record<Size, { padding: string; fontSize: number }> = {
  sm: { padding: "8px 14px",  fontSize: 13 },
  md: { padding: "14px 22px", fontSize: 15 },
  lg: { padding: "16px 24px", fontSize: 16 },
};

const VARIANT: Record<Variant, React.CSSProperties> = {
  primary:   { background: "var(--brand-orange)", color: "#fff", boxShadow: "var(--sh-brand)" },
  secondary: { background: "var(--bg-secondary)", color: "var(--fg1)" },
  ghost:     { background: "transparent", color: "var(--brand-orange)" },
  danger:    { background: "transparent", color: "var(--fg-danger)" },
};

export function Button({
  variant = "primary",
  size = "md",
  fullWidth,
  leading,
  trailing,
  children,
  disabled,
  onClick,
  style,
  ...rest
}: Props) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        if (!disabled) {
          haptic("light");
          onClick?.(e);
        }
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        border: 0,
        fontFamily: "var(--font-body)",
        fontWeight: 600,
        borderRadius: 999,
        transition: "all var(--d-fast) var(--ease-out)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        width: fullWidth ? "100%" : undefined,
        ...SIZE[size],
        ...VARIANT[variant],
        ...style,
      }}
      {...rest}
    >
      {leading}
      <span>{children}</span>
      {trailing}
    </button>
  );
}
